import * as React from 'react';

import {
  Button,
  Box,
  Card,
  CardActions,
  CardContent,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';

import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';

import { DateTime } from 'luxon';

import { AllowedActions, Attachment, ReportType } from '../core/api/clients';

import useAddAttachment, { IAttachmentMutationVariables } from '../core/api/hooks/attachment/useAddAttachment';
import useDeleteAttachment from '../core/api/hooks/attachment/useDeleteAttachment';
import useGetAttachmentsForEventReport from '../core/api/hooks/attachment/useGetAttachmentsForEventReport';

interface IAttachmentList {
  reportType: ReportType;
  reportId: number | undefined;
  allowedActions: AllowedActions | undefined;
  disableUpload?: boolean;
  hideUpload?: boolean;
}

function AttachmentList({ reportType, reportId, allowedActions, disableUpload, hideUpload }: IAttachmentList) {
  const { data: uploadedAttachments, refetch: refetchUploadedAttachments } = useGetAttachmentsForEventReport(reportId, reportType);
  const { mutate: addAttachment } = useAddAttachment();
  const { mutate: deleteAttachment } = useDeleteAttachment();

  const [attachmentInProgress, setAttachmentInProgress] = React.useState<IAttachmentMutationVariables | null>(null);
  const [requestedDelete, setRequestedDelete] = React.useState<number | null | undefined>();

  const [hasError, setHasError] = React.useState<boolean>();

  React.useEffect(() => {
    if (attachmentInProgress !== null) {
      addAttachment(attachmentInProgress, {
        onSuccess: () => {
          refetchUploadedAttachments().then(() => setAttachmentInProgress(null));
        },
        onError: (error, variables, context) => {
          setAttachmentInProgress(null);
          setHasError(true);
        },
      });
    }
  }, [attachmentInProgress]);

  React.useEffect(() => {
    if (requestedDelete !== undefined && requestedDelete !== null) {
      deleteAttachment(requestedDelete, {
        onSuccess: () => {
          refetchUploadedAttachments().then(() => setRequestedDelete(undefined));
        },
        onError: (error) => {
          setRequestedDelete(undefined);
          setHasError(true);
        },
      });
    }
  }, [requestedDelete]);

  const handleListItemClick = (attachment: Attachment) => {
    window.open(`api/attachment/get-attachment/${attachment.id}/${attachment.fileName}`);
  };

  const handleDeleteClick = (attachmentId: number | null | undefined) => {
    setRequestedDelete(attachmentId);
  };

  const handleUploadChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file !== null && reportId !== undefined) {
      setAttachmentInProgress({
        reportType,
        reportId,
        size: file.size,
        fileName: file.name,
        formFile: {
          data: file,
          fileName: file.name,
        },
      });
    }
    e.target.value = '';
  };

  const getFormattedDate = (date: string | null | undefined): string => DateTime.fromISO(date ?? '').toHTTP() ?? '-';

  const getSize = (size: number | null | undefined): string => {
    if (size !== undefined && size !== null) {
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
      const i = Math.floor(Math.log(size) / Math.log(1024));

      return `${parseFloat((size / 1024 ** i).toFixed(2))} ${sizes[i]}`;
    }

    return '-';
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <List subheader="Uploaded Attachments">
          {uploadedAttachments ? uploadedAttachments.map((attachment: Attachment) => (
            <ListItem
              key={attachment.id}
              secondaryAction={(
                <IconButton disabled={requestedDelete !== undefined || !allowedActions?.edit} onClick={() => handleDeleteClick(attachment.id)}>
                  <DeleteIcon />
                </IconButton>
              )}
            >
              <ListItemButton
                hidden={!allowedActions?.edit}
                disabled={requestedDelete === attachment.id}
                onClick={() => handleListItemClick(attachment)}
              >
                <ListItemText
                  primary={attachment.fileName}
                  secondary={`Uploaded: ${getFormattedDate(attachment.uploadedDate)} Size: ${getSize(attachment.size)}`}
                />
                <Box hidden={requestedDelete !== attachment.id} sx={{ width: 100, height: 20 }}>
                  <LinearProgress sx={{ width: 100, height: 20 }} />
                </Box>
              </ListItemButton>
            </ListItem>
          )) : null}
          {attachmentInProgress !== null && (
            <ListItem>
              <ListItemButton>
                <ListItemText
                  primary={attachmentInProgress.fileName}
                />
                <Box sx={{ width: 100, height: 20 }}>
                  <LinearProgress sx={{ width: 100, height: 20 }} />
                </Box>
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </CardContent>
      {allowedActions?.edit && !hideUpload && (
        <CardActions>
          <Button
            startIcon={<FileUploadIcon />}
            component="label"
            disabled={!allowedActions?.edit}
            hidden={!allowedActions?.edit}
          >
            Upload
            <input
              hidden
              accept="audio/*,video/*,image/*,application/pdf"
              type="file"
              onChange={handleUploadChange}
            />
          </Button>
        </CardActions>
      )}
    </Card>
  );
}

export default AttachmentList;
